import { AUTH_PROVIDERS, type RefreshResponse } from "@workcost/shared";
import { API_URL } from "./env";

/**
 * Auth client
 * - access token เก็บใน memory เท่านั้น (ไม่แตะ localStorage / sessionStorage / IndexedDB)
 * - refresh token เป็น cookie httpOnly ที่ server ตั้งให้ ใช้ผ่าน POST /auth/refresh
 */

export const CSRF_HEADER = "x-workcost-csrf";
/** ต่ออายุก่อนหมดอายุกี่มิลลิวินาที */
const SKEW_MS = 30_000;
/** กันหน้าค้างที่ "กำลังตรวจสอบ" เมื่อเครือข่ายช้ามาก */
const REFRESH_TIMEOUT_MS = 8_000;

export type AuthProviderName = (typeof AUTH_PROVIDERS)[number];
export type RefreshResult = { status: "ok"; accessToken: string } | { status: "anonymous" } | { status: "error" };

let accessToken: string | null = null;
let expiresAt = 0;
let inflight: Promise<RefreshResult> | null = null;
const listeners = new Set<(token: string | null) => void>();

function emit(): void {
  for (const l of listeners) l(accessToken);
}

export function onAuthChange(cb: (token: string | null) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function setAccessToken(token: string | null, expiresInSec = 900): void {
  accessToken = token;
  expiresAt = token ? Date.now() + expiresInSec * 1000 : 0;
  emit();
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getExpiresAt(): number {
  return expiresAt;
}

/** ขอ access token ใหม่จาก cookie refresh (รวมการเรียกซ้อนให้เหลือครั้งเดียว) */
export function refreshSession(fetchImpl: typeof fetch = (...a) => fetch(...a)): Promise<RefreshResult> {
  if (inflight) return inflight;
  inflight = (async (): Promise<RefreshResult> => {
    try {
      const res = await fetchImpl(`${API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { [CSRF_HEADER]: "1" },
        signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
      });
      if (res.status === 401 || res.status === 403) {
        setAccessToken(null);
        return { status: "anonymous" };
      }
      if (!res.ok) return { status: "error" };
      const body = (await res.json()) as RefreshResponse;
      if (!body || typeof body.accessToken !== "string") return { status: "error" };
      setAccessToken(body.accessToken, body.expiresIn);
      return { status: "ok", accessToken: body.accessToken };
    } catch {
      return { status: "error" };
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** คืน token ที่ใช้ได้ ถ้าใกล้หมดอายุจะ refresh ให้ก่อน */
export async function getValidToken(): Promise<string | null> {
  if (accessToken && Date.now() < expiresAt - SKEW_MS) return accessToken;
  const r = await refreshSession();
  return r.status === "ok" ? r.accessToken : null;
}

/** กันการ redirect ไปที่อื่น: รับเฉพาะ path ภายในเว็บ */
export function sanitizeReturnTo(raw: string | null | undefined, fallback = "/"): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.includes("\\")) return fallback;
  return raw;
}

export function loginUrl(provider: AuthProviderName, returnTo: string): string {
  return `${API_URL}/auth/login?provider=${encodeURIComponent(provider)}&returnTo=${encodeURIComponent(sanitizeReturnTo(returnTo))}`;
}

/** ไปหน้า login ของ BFF (full page redirect) */
export function startLogin(provider: AuthProviderName, returnTo: string): void {
  window.location.assign(loginUrl(provider, returnTo));
}

export async function logout(fetchImpl: typeof fetch = (...a) => fetch(...a)): Promise<boolean> {
  try {
    const res = await fetchImpl(`${API_URL}/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: { [CSRF_HEADER]: "1" },
    });
    if (!res.ok) return false;
    setAccessToken(null);
    return true;
  } catch {
    return false;
  }
}

/** สำหรับทดสอบ */
export function resetAuthForTests(): void {
  accessToken = null;
  expiresAt = 0;
  inflight = null;
  listeners.clear();
}
